import { useState, useEffect } from "react";
import { api } from "../api";
import {
  companyInfo as mockCompany,
  whyChooseUs as mockWhyChooseUs,
  services as mockServices,
  products as mockProducts,
  funeralAid as mockFuneralAid,
  blogPosts as mockBlogPosts,
  galleryImages as mockGalleryImages,
  googleProfile as mockGoogleProfile,
  testimonials as mockTestimonials,
} from "../data/mock";

function useApiData(fetchFn, fallback, deps = []) {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchFn()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { /* fall back to mock data silently */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading };
}

// ─── Company / site-wide content ───
let _companyCache = null;
let _companyPromise = null;

function fetchCompanyCached() {
  if (_companyCache) return Promise.resolve(_companyCache);
  if (!_companyPromise) {
    _companyPromise = api.getCompany().then((d) => {
      _companyCache = d;
      return d;
    });
  }
  return _companyPromise;
}

export function useCompany() {
  const fallback = {
    companyInfo: mockCompany,
    whyChooseUs: mockWhyChooseUs,
    galleryImages: mockGalleryImages,
    googleProfile: mockGoogleProfile,
    testimonials: mockTestimonials,
    funeralAid: mockFuneralAid,
  };
  return useApiData(fetchCompanyCached, fallback);
}

// ─── Services ───
export function useServices() {
  return useApiData(api.getServices, mockServices);
}

export function useService(slug) {
  const fallback = mockServices.find((s) => s.slug === slug) || null;
  return useApiData(() => api.getService(slug), fallback, [slug]);
}

// ─── Blog ───
export function useBlogPosts() {
  return useApiData(api.getBlogPosts, mockBlogPosts);
}

export function useBlogPost(slug) {
  const fallback = mockBlogPosts.find((p) => p.slug === slug) || null;
  return useApiData(() => api.getBlogPost(slug), fallback, [slug]);
}

// ─── Products ───
export function useProducts() {
  return useApiData(api.getProducts, mockProducts);
}
